import React, { useState, useCallback, useContext, useMemo } from 'react';
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonLabel,
  IonText,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonImg,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { TranslationsContext } from '../util/Translations';
import { InspectionPointImage, PointDefType } from '../models/inspections/Inspections';
import './PointAttachments.css';

const buildDataUrlFromPayload = (payload: string, extension?: string): string => {
  if (!payload) {
    return '';
  }
  if (
    payload.startsWith('data:') ||
    payload.startsWith('http') ||
    payload.startsWith('blob:')
  ) {
    return payload;
  }
  const sanitizedExt =
    extension?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpeg';
  return `data:image/${sanitizedExt};base64,${payload}`;
};

interface PointAttachmentsComponentProps {
  pointId: number;
  columnId: string;
  pointImages: { [key: string]: InspectionPointImage[] };
  pointComments: { [key: string]: string };
  onImagesChange: (images: { [key: string]: InspectionPointImage[] }) => void;
  pointDefs?: PointDefType[];
  disableInsp?: boolean;
}

const PointAttachmentsComponent: React.FC<PointAttachmentsComponentProps> = ({
  pointId,
  columnId,
  pointImages,
  pointComments,
  onImagesChange,
  pointDefs = [],
  disableInsp = false
}) => {
  const { translations } = useContext(TranslationsContext);
  
  const [showLargeImageModal, setShowLargeImageModal] = useState(false);
  const [showCommentViewModal, setShowCommentViewModal] = useState(false);

  const [currentLargeImage, setCurrentLargeImage] = useState<string>('');
  const [currentViewComment, setCurrentViewComment] = useState<string>('');

  const key = useMemo(() => {
    const parts = columnId?.split("|") ?? [];
    const columnCode = parts.at(-1) ?? columnId;
    return `${pointId}_${columnCode}`;
  }, [pointId, columnId]);
  const images = useMemo(() => pointImages[key] || [], [pointImages, key]);
  const comment = useMemo(() => pointComments[key] || '', [pointComments, key]);
  const commentPreview = useMemo(() => {
    if (!comment) {
      return '';
    }
    return comment.length > 50 ? `${comment.substring(0, 50)} ...` : comment;
  }, [comment]);
  
  const pointDef = useMemo(() => pointDefs.find(p => p.PointId === pointId), [pointDefs, pointId]);
  const templateImages = useMemo(() => {
    const sourceImages = pointDef?.Images ?? pointDef?.Image ?? [];
    if (!Array.isArray(sourceImages)) {
      return [];
    }
    return sourceImages.map((img) => {
      const rawPayload = img.InspectionPointImage ?? img.ImageType ?? '';
      const normalized = buildDataUrlFromPayload(rawPayload, img.ImageExtension);
      return {
        ...img,
        ImageType: normalized,
        InspectionPointImage: normalized,
      } as InspectionPointImage;
    });
  }, [pointDef]);

  const handleRemoveImage = useCallback((imageIndex: number) => {
    const newImages = {
      ...pointImages,
      [key]: pointImages[key]?.filter((_, index) => index !== imageIndex) || [],
    };
    onImagesChange(newImages);
  }, [pointImages, key, onImagesChange]);

  const handleShowLargeImage = useCallback((imageSrc: string) => {
    setCurrentLargeImage(imageSrc);
    setShowLargeImageModal(true);
  }, []);

  const handleShowFullComment = useCallback((commentText: string) => {
    setCurrentViewComment(commentText);
    setShowCommentViewModal(true);
  }, []);
  const commentModalBreakpoint = useMemo(() => {
    if (!currentViewComment) {
      return 0.5;
    }
    return currentViewComment.length > 120 ? 0.8 : 0.5;
  }, [currentViewComment]);

  const commentModalBreakpoints = useMemo(() => {
    if (!currentViewComment || currentViewComment.length <= 120) {
      return [0, 0.5, 0.8];
    }
    return [0, 0.5, 0.8, 1];
  }, [currentViewComment]);


  const handleCloseModal = useCallback(() => {
    setShowLargeImageModal(false);
    setShowCommentViewModal(false);
  }, []);

  return (
    <>
      {templateImages.length > 0 && (
        <fieldset>
          <legend style={{ color: 'var(--ion-color-primary)' }}>
            {translations['lbl_reference_images'] || 'Reference Images'}
          </legend>
        <IonGrid className="point-attachments-template-wrapper">
          <IonRow className="point-attachments-image-container">
            {templateImages.map((templateImage: InspectionPointImage) => {
              const imageSrc =
                templateImage?.InspectionPointImage ?? templateImage.ImageType;
              const uniqueKey = `template-${pointId}-${columnId}-${imageSrc
                ?.toString()
                .split("/")
                .pop()}-${imageSrc}`;
              return (
                <IonCol
                  key={uniqueKey}
                  size="auto"
                  className="point-attachments-image-wrapper"
                >
                  <IonButton
                    fill="clear"
                    className="point-attachments-template-button"
                    onClick={() => handleShowLargeImage(imageSrc)}
                    aria-label={`View template image: ${templateImage.AltText}`}
                  >
                    <IonImg
                      src={imageSrc}
                      alt={templateImage.AltText}
                      className="inspection-image-thumbnail"
                    />
                  </IonButton>
                </IonCol>
              );
            })}
          </IonRow>
        </IonGrid>
        </fieldset>
      )}

      {images.length > 0 && (
        <fieldset>
          <legend style={{ color: 'var(--ion-color-primary)' }}>
            {translations['lbl_inspection_images'] || 'Inspection Images'}
          </legend>
        <IonGrid className="point-attachments-user-wrapper">
          <IonRow className="point-attachments-image-container">
            {images.map((image: InspectionPointImage, index: number) => {
              const imageSrc =
                image?.InspectionPointImage ?? image.ImageType;
              return (
                <IonCol
                  key={`user-${pointId}-${columnId}-${index}`}
                  size="auto"
                  className="point-attachments-image-wrapper"
                >
                  <IonButton
                    fill="clear"
                    className="point-attachments-thumbnail-button"
                    onClick={() => handleShowLargeImage(imageSrc)}
                    aria-label={`View large image: ${image.AltText}`}
                  >
                    <IonImg
                      src={imageSrc}
                      alt={image.AltText}
                      className="inspection-image-thumbnail"
                    />
                  </IonButton>
                  {!disableInsp && !image?.ReadOnly && (
                    <IonButton
                      fill="clear"
                      size="small"
                      className="inspection-image-remove-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      <IonIcon icon={close} size="small" />
                    </IonButton>
                  )}
                </IonCol>
              );
            })}
          </IonRow>
        </IonGrid>
        </fieldset>
      )}
      {comment && (
        <IonItem
          color="light"
          className="inspection-comment-box"
          style={{
            marginBottom: '4px',
            cursor: 'pointer'
          }}
          button
          onClick={() => handleShowFullComment(comment)}
        >
          <IonLabel className="inspection-comment-label">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="inspection-comment-preview">
                {commentPreview}
              </span>
            </div>
          </IonLabel>
        </IonItem>
      )}
      
      <IonModal
        isOpen={showLargeImageModal}
        onDidDismiss={handleCloseModal}
        breakpoints={[0, 0.5, 0.8]}
        initialBreakpoint={0.5}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{translations['lbl_image_view'] || 'Image View'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleCloseModal}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonItem lines="none" style={{ textAlign: 'center' }}>
            <img
              src={currentLargeImage}
              alt="Large view"
              className="inspection-large-image"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </IonItem>
        </IonContent>
      </IonModal>

      <IonModal
        isOpen={showCommentViewModal}
        onDidDismiss={handleCloseModal}
        breakpoints={commentModalBreakpoints}
        initialBreakpoint={commentModalBreakpoint}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{translations['lbl_view_comment'] || 'View Comment'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleCloseModal}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent scrollY>
          <IonItem
            lines="none"
            style={{
              padding: '16px',
              maxHeight: '60vh',
              overflowY: 'auto',
              width: '100%'
            }}
          >
            <IonLabel>
              <IonText
                className="inspection-comment-view"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%' }}
              >
                {currentViewComment}
              </IonText>
            </IonLabel>
          </IonItem>
        </IonContent>
      </IonModal>
    </>
  );
};

export default PointAttachmentsComponent;